import { Location } from '@angular/common';
import {
  Component,
  DestroyRef,
  inject,
  OnInit,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { PaymentType } from '@basket';
import { CheckoutDirectDebitAccountDetails, CheckoutResponse } from '@checkout';
import { JourneyStepTrackerService } from '@common';
import {
  GridColumnComponent,
  GridContainerComponent,
  GridRowComponent,
  ProgressionStepsComponent,
} from '@components';
import {
  BasketComponent,
  BasketStickyWrapperComponent,
  GADataService,
  OverlayService,
  OverlayState,
} from '@data-access';
import { CheckoutService } from '@features/checkout';
import { ErrorRoutes } from '@features/error';
import { MultiPlanService } from '@features/multi-plan';
import { OrderConfirmationRoutes } from '@features/order-confirmation';
import {
  provideTranslationDictionaryMapping,
  TranslatePipe,
  TranslateService,
} from '@i18n';
import { PosthogTrackingEvents, TrackingService } from '@tracking';
import posthog from 'posthog-js';
import { catchError, filter, finalize, switchMap, tap } from 'rxjs';
import { DirectDebitFormComponent } from '../../components/direct-debit-form/direct-debit-form.component';
import { FeatureCheckoutTranslationDictionary } from '../../i18n/feature-checkout-dictionary.interface';
import { translation } from '../../i18n/translation.constant';
import { DirectDebitFormValue } from '../../models/direct-debit/direct-debit-form-value.type';

@Component({
  selector: 'app-checkout',
  imports: [
    BasketComponent,
    BasketStickyWrapperComponent,
    DirectDebitFormComponent,
    GridColumnComponent,
    GridContainerComponent,
    GridRowComponent,
    ProgressionStepsComponent,
    ReactiveFormsModule,
    TranslatePipe,
  ],
  templateUrl: './checkout.component.html',
  styleUrl: './checkout.component.scss',
  providers: [provideTranslationDictionaryMapping(translation)],
})
export class CheckoutComponent implements OnInit {
  private readonly checkoutService = inject(CheckoutService);
  private readonly multiPlanService = inject(MultiPlanService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly journeyStepTracker = inject(JourneyStepTrackerService);
  private readonly router = inject(Router);
  private readonly trackingService = inject(TrackingService);
  private readonly gaDataService = inject(GADataService);
  private readonly overlayService = inject(OverlayService);
  private readonly translateService = inject(
    TranslateService<FeatureCheckoutTranslationDictionary>,
  );
  private readonly location = inject(Location);

  @ViewChild(DirectDebitFormComponent)
  directDebitForm!: DirectDebitFormComponent;

  private validateAccountDetails(
    accountNumber: string,
    sortCode: string,
  ): void {
    this.overlayService.show(OverlayState.Checkout);

    this.checkoutService
      .validateAccountNumberSortCode({ accountNumber, sortCode })
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap((isValid) => {
          this.checkoutService.setValidAccountNumberSortCode(isValid);
          this.directDebitForm.setValidationAttempted();
          if (!isValid) {
            this.directDebitForm.resetSubmissionState();
          }
        }),
        filter((isValid) => isValid),
        switchMap(() => this.checkoutService.checkout()),
        tap((checkoutResponse: CheckoutResponse) => {
          if (checkoutResponse) {
            if (this.multiPlanService.selectedAppliancesDetails().length > 0) {
              posthog.capture(PosthogTrackingEvents.MultiPlanCheckoutSuccess);
            } else {
              posthog.capture(PosthogTrackingEvents.SinglePlanCheckoutSuccess);
            }

            this.trackingService.trackEvent(
              this.gaDataService.eventDirectDebitDetails(),
            );
            this.router.navigate([OrderConfirmationRoutes.OrderConfirmation]);
          }
        }),
        catchError(() => this.router.navigate([ErrorRoutes.Error])),
        finalize(() => this.overlayService.hide()),
      )
      .subscribe();
  }

  ngOnInit(): void {
    this.journeyStepTracker.updateStep(
      3,
      3,
      this.translateService.translate('STEP_TITLE_YOUR_PAYMENT_DETAILS'),
      false,
    );

    if (this.multiPlanService.selectedAppliancesDetails().length > 0) {
      posthog.capture(PosthogTrackingEvents.MultiPlanDirectDebitPageLoaded);
    } else {
      posthog.capture(PosthogTrackingEvents.SinglePlanDirectDebitPageLoaded);
    }
  }

  onFormSubmit(formValue: DirectDebitFormValue): void {
    this.setCheckoutDirectDebitPaymentDetails(formValue);

    if (formValue.accountNumber && formValue.sortCode) {
      this.validateAccountDetails(formValue.accountNumber, formValue.sortCode);
    }
  }

  navigateBack(): void {
    this.location.back();
  }

  private setCheckoutDirectDebitPaymentDetails(
    formValue: DirectDebitFormValue,
  ): void {
    if (
      formValue.accountName &&
      formValue.accountNumber &&
      formValue.sortCode
    ) {
      const preferredPaymentDay = Number(formValue.paymentDate);
      const directDebitDetails: CheckoutDirectDebitAccountDetails = {
        accountName: formValue.accountName,
        accountNumber: formValue.accountNumber,
        sortCode: formValue.sortCode,
      };

      this.checkoutService.setCheckoutDirectDebitPaymentDetails({
        directDebitDetails,
        paymentType: PaymentType.DirectDebit,
        preferredPaymentDay,
      });
    }
  }
}
